import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { BranchDto } from '../lib/api'
import { SteiermarkMiniMap } from './SteiermarkMiniMap'
import craftImg from '../assets/barber-craft.jpg'
import './HomeBranchesSection.css'

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

function displayBranchName(name: string): string {
  return name.replace(/^ILIA\s+/i, '').trim() || name
}

function branchAddress(branch: BranchDto): string {
  return [branch.addressLine, [branch.postalCode, branch.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
}

function isBranchOpenNow(hours: BranchDto['openingHours']): boolean | null {
  if (!hours || Object.keys(hours).length === 0) return null
  try {
    const now = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'Europe/Vienna' }),
    )
    const key = DAY_KEYS[now.getDay()]
    const slot = hours[key]
    if (slot == null) return false
    if (!slot.open || !slot.close) return null
    const [oh, om] = slot.open.split(':').map(Number)
    const [ch, cm] = slot.close.split(':').map(Number)
    const mins = now.getHours() * 60 + now.getMinutes()
    const openMins = oh * 60 + (om || 0)
    const closeMins = ch * 60 + (cm || 0)
    return mins >= openMins && mins < closeMins
  } catch {
    return null
  }
}

function mapsDirectionsUrl(branch: BranchDto): string {
  if (branch.googleMapsUrl) return branch.googleMapsUrl
  if (typeof branch.latitude === 'number' && typeof branch.longitude === 'number') {
    return `https://www.google.com/maps/dir/?api=1&destination=${branch.latitude},${branch.longitude}`
  }
  const q = encodeURIComponent(
    [displayBranchName(branch.name), branch.addressLine, branch.city, 'Österreich']
      .filter(Boolean)
      .join(', '),
  )
  return `https://www.google.com/maps/search/?api=1&query=${q}`
}

export type HomeBranchesSectionProps = {
  branches: BranchDto[]
  loading?: boolean
  error?: boolean
  fallbackImage?: string
}

export function HomeBranchesSection({
  branches,
  loading,
  error,
  fallbackImage = craftImg,
}: HomeBranchesSectionProps) {
  const { t, i18n } = useTranslation()
  const active = branches.filter((b) => b.isActive !== false)
  const rtl = i18n.language === 'ar' || document.documentElement.getAttribute('dir') === 'rtl'

  const primaryMapsUrl =
    active[0] ? mapsDirectionsUrl(active[0]) : 'https://www.google.com/maps/search/?api=1&query=ILIA+Barber+Steiermark'

  return (
    <section className="home-branches-section" id="ilia" aria-labelledby="home-branches-title">
      <div className="container home-branches-shell">
        <header className="home-branches-intro">
          <p className="home-branches-eyebrow">{t('sections.branches')}</p>
          <div className="home-branches-intro-row">
            <h2 id="home-branches-title">{t('homeBranches.title')}</h2>
            <p className="home-branches-lead">{t('homeBranches.lead')}</p>
          </div>
        </header>

        <div className="home-branches-layout">
          <div className="home-branches-main">
            {loading && <div className="skeleton home-branches-loading" />}
            {error && <div className="error-state">{t('common.error')}</div>}
            {!loading && !error && active.length === 0 && (
              <p className="home-branches-empty">{t('common.empty')}</p>
            )}

            {!loading && !error && active.length > 0 && (
              <div
                className="home-branches-rail"
                role="list"
                dir={rtl ? 'rtl' : 'ltr'}
              >
                {active.map((branch) => {
                  const open = isBranchOpenNow(branch.openingHours)
                  return (
                    <article key={branch.id} className="home-branch-card" role="listitem">
                      <div className="home-branch-card-media">
                        <img
                          src={branch.imageUrl || fallbackImage}
                          alt={displayBranchName(branch.name)}
                          loading="lazy"
                          decoding="async"
                          width={480}
                          height={320}
                        />
                        {open != null ? (
                          <span className={`home-branch-status${open ? ' is-open' : ' is-closed'}`}>
                            {open ? t('homeBranches.openNow') : t('homeBranches.closedNow')}
                          </span>
                        ) : null}
                      </div>
                      <div className="home-branch-card-body">
                        <h3>{displayBranchName(branch.name)}</h3>
                        <p className="home-branch-card-city">{branch.city || 'Steiermark'}</p>
                        {branch.addressLine ? (
                          <p className="home-branch-card-meta">{branchAddress(branch)}</p>
                        ) : null}
                        <div className="home-branch-card-actions">
                          <Link
                            className="btn home-branch-card-cta"
                            to={`/book?branch=${encodeURIComponent(branch.id)}`}
                          >
                            {t('homeBranches.bookNow')}
                          </Link>
                          <a
                            className="home-branch-card-maps"
                            href={mapsDirectionsUrl(branch)}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {t('homeBranches.viewLocation')}
                          </a>
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </div>

          <aside className="home-branches-map-panel">
            <p className="home-branches-map-title">{t('homeBranches.mapCaption')}</p>
            <div className="home-branches-map-card">
              <div className="home-branches-map-frame">
                <SteiermarkMiniMap
                  branches={active}
                  interactive
                  bookLabel={t('homeBranches.bookNow')}
                  bookPath={(b) => `/book?branch=${encodeURIComponent(b.id)}`}
                  mapsLabel={t('homeBranches.openRoute')}
                  mapsUrl={mapsDirectionsUrl}
                />
              </div>
            </div>
            <div className="home-branches-map-actions">
              <Link className="btn home-branches-map-btn" to="/filialen">
                {t('homeBranches.allBranches')}
              </Link>
              <a
                className="btn home-branches-map-btn is-secondary"
                href={primaryMapsUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('homeBranches.openRoute')}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}
